import * as React from 'react';
import { AuthActionCreators } from '@nkr/store/index';
import { useAppHookContext } from '@nkr/utils/rest-api/useAppHookContext';
import { ITemplate } from './index';

interface IProps {
    templates: ITemplate[];
}

interface IResult {
    success: boolean;
    message: string;
    templateType?: string;
}

const GROUP_LABELS: Record<string, string> = {
    DonorIntake:   'Donor Intake (1-19)',
    FollowUp:      'Follow-Up (20-34)',
    LabUpload:     'Lab Upload (35-38)',
    DonorShield:   'Donor Shield (39-42)',
    Internal:      'Internal (43-45)',
    DashDonor:     'Dash Donor (46-54)',
    Auth:          'Auth (55-57)',
    Dcn:           'DCN (58-60)',
    Misc:          'Misc / Referral (61-69)',
    StrongBox:     'Strong Box (70-72)',
    Watr:          'WATR (73-87)',
    Serology:      'Serology / XM (88-93)',
    Billing:       'Billing (94-96)',
    Pass:          'PASS (97-106)',
    HomeBloodDraw: 'Home Blood Draw (107-109)',
    Invoicing:     'Invoicing (110-115)',
    Centers:       'Centers (116-124)',
    Kpd:           'KPD (125-190)',
    Microsites:    'Microsites (191-229)',
    Remaining:     'Remaining (230-266)',
    Extra:         'Extra (290)'
};

const SingleSendForm = ({ templates }: IProps) => {
    const { dispatch } = useAppHookContext();
    const [templateType, setTemplateType] = React.useState<number>(0);
    const [emailAddress, setEmailAddress] = React.useState('nkrtestemail@gmail.com');
    const [firstName, setFirstName] = React.useState('John');
    const [lastName, setLastName] = React.useState('Doe');
    const [centerName, setCenterName] = React.useState('Test Transplant Center');
    const [organType, setOrganType] = React.useState<0 | 1>(0);
    const [sending, setSending] = React.useState(false);
    const [result, setResult] = React.useState<IResult | null>(null);

    const selectedTemplate = templates.find(t => t.id === templateType);
    const isLiverCapable = selectedTemplate?.liverCapable ?? false;

    React.useEffect(() => {
        if (!isLiverCapable && organType === 1) {
            setOrganType(0);
        }
    }, [isLiverCapable]);

    const grouped = React.useMemo(() => {
        const map: Record<string, ITemplate[]> = {};
        for (const t of templates) {
            if (!map[t.group]) map[t.group] = [];
            map[t.group].push(t);
        }
        return map;
    }, [templates]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!templateType || !emailAddress.trim()) return;

        setSending(true);
        setResult(null);

        try {
            const res: any = await dispatch(AuthActionCreators.apiPost({
                url: __API__ + '/admin/email-test-harness/send',
                data: {
                    templateType,
                    emailAddress: emailAddress.trim(),
                    firstName: firstName || null,
                    lastName: lastName || null,
                    centerName: centerName || null,
                    organType
                }
            }));
            setResult(res.data);
        } catch (err: any) {
            const msg = err?.response?.data?.message ?? 'An unexpected error occurred.';
            setResult({ success: false, message: msg });
        } finally {
            setSending(false);
        }
    };

    const groupOrder = [
        'DonorIntake', 'FollowUp', 'LabUpload', 'DonorShield', 'Internal', 'DashDonor',
        'Auth', 'Dcn', 'Misc', 'StrongBox', 'Watr', 'Serology', 'Billing', 'Pass',
        'HomeBloodDraw', 'Invoicing', 'Centers', 'Kpd', 'Microsites', 'Remaining', "Extra"
    ];

    return (
        <form onSubmit={handleSubmit}>
            {result && (
                <div className={`alert alert-${result.success ? 'success' : 'danger'}`} style={{ marginBottom: 16 }}>
                    {result.message}
                </div>
            )}

            <div className='row'>
                <div className='col-md-6'>
                    <div className='form-group'>
                        <label>Email Template *</label>
                        <select
                            className='form-control'
                            value={templateType}
                            onChange={e => setTemplateType(Number(e.target.value))}
                            required
                        >
                            <option value={0}>-- Select a template --</option>
                            {groupOrder.filter(g => grouped[g]).map(group => (
                                <optgroup key={group} label={GROUP_LABELS[group] ?? group}>
                                    {grouped[group].map(t => (
                                        <option key={t.id} value={t.id}>
                                            {t.id}. {t.name}
                                        </option>
                                    ))}
                                </optgroup>
                            ))}
                        </select>
                    </div>
                </div>
                <div className='col-md-6'>
                    <div className='form-group'>
                        <label>Test Email Address *</label>
                        <input
                            type='email'
                            className='form-control'
                            value={emailAddress}
                            onChange={e => setEmailAddress(e.target.value)}
                            placeholder='nkrtestemail@gmail.com'
                            required
                        />
                    </div>
                </div>
            </div>

            <div className='row'>
                <div className='col-md-12'>
                    <div className='form-group'>
                        <label>Organ Type</label>
                        {!isLiverCapable && templateType !== 0 && (
                            <span className='text-muted' style={{ marginLeft: 8, fontSize: 12 }}>(Kidney only)</span>
                        )}
                        <div>
                            <label className='radio-inline'>
                                <input type='radio' checked={organType === 0} onChange={() => setOrganType(0)} />
                                {' '}Kidney
                            </label>
                            {' '}
                            <label className='radio-inline' style={{ opacity: isLiverCapable ? 1 : 0.4 }}>
                                <input type='radio' checked={organType === 1} onChange={() => setOrganType(1)}
                                    disabled={!isLiverCapable} />
                                {' '}Liver
                            </label>
                        </div>
                    </div>
                </div>
            </div>

            <div className='row'>
                <div className='col-md-4'>
                    <div className='form-group'>
                        <label>First Name <span className='text-muted'>(optional)</span></label>
                        <input
                            type='text'
                            className='form-control'
                            value={firstName}
                            onChange={e => setFirstName(e.target.value)}
                            placeholder='Override first name'
                        />
                    </div>
                </div>
                <div className='col-md-4'>
                    <div className='form-group'>
                        <label>Last Name <span className='text-muted'>(optional)</span></label>
                        <input
                            type='text'
                            className='form-control'
                            value={lastName}
                            onChange={e => setLastName(e.target.value)}
                            placeholder='Override last name'
                        />
                    </div>
                </div>
                <div className='col-md-4'>
                    <div className='form-group'>
                        <label>Center Name <span className='text-muted'>(optional)</span></label>
                        <input
                            type='text'
                            className='form-control'
                            value={centerName}
                            onChange={e => setCenterName(e.target.value)}
                            placeholder='Override center name'
                        />
                    </div>
                </div>
            </div>

            <div>
                <button
                    type='submit'
                    className='btn btn-primary'
                    disabled={sending || !templateType || !emailAddress.trim()}
                >
                    {sending ? 'Sending...' : 'Send Test Email'}
                </button>
                {' '}
                <button
                    type='button'
                    className='btn btn-default'
                    onClick={() => {
                        setTemplateType(0);
                        setEmailAddress('nkrtestemail@gmail.com');
                        setFirstName('John');
                        setLastName('Doe');
                        setCenterName('Test Transplant Center');
                        setOrganType(0);
                        setResult(null);
                    }}
                >
                    Reset
                </button>
            </div>
        </form>
    );
};

export default SingleSendForm;
